import { useState, useEffect, useCallback } from 'react'
import { X, Calendar, Clock } from 'lucide-react'

interface RescheduleModalProps {
  isOpen: boolean
  onClose: () => void
  currentDate: string
  currentTime?: string
  onReschedule: (date: string, time?: string) => void
  isPending?: boolean
}

export function RescheduleModal({
  isOpen,
  onClose,
  currentDate,
  currentTime,
  onReschedule,
  isPending = false,
}: RescheduleModalProps) {
  // Use key-based remount strategy - content is only rendered when open
  // Key includes currentDate to force remount when switching between recordings
  if (!isOpen) return null

  return (
    <RescheduleModalContent
      key={`${currentDate}-${currentTime || ''}`}
      onClose={onClose}
      currentDate={currentDate}
      currentTime={currentTime}
      onReschedule={onReschedule}
      isPending={isPending}
    />
  )
}

interface RescheduleModalContentProps {
  onClose: () => void
  currentDate: string
  currentTime?: string
  onReschedule: (date: string, time?: string) => void
  isPending: boolean
}

function RescheduleModalContent({
  onClose,
  currentDate,
  currentTime,
  onReschedule,
  isPending,
}: RescheduleModalContentProps) {
  const [date, setDate] = useState(currentDate)
  const [time, setTime] = useState(currentTime || '')

  // Handle Escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!date) return
    onReschedule(date, time || undefined)
  }

  const hasChanges = date !== currentDate || time !== (currentTime || '')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reschedule-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2
            id="reschedule-modal-title"
            className="text-lg font-semibold text-zinc-900 dark:text-zinc-100"
          >
            Reschedule Recording
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Date */}
            <div>
              <label
                htmlFor="reschedule-date"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Recording Date
                </span>
              </label>
              <input
                id="reschedule-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={isPending}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                required
              />
            </div>

            {/* Time */}
            <div>
              <label
                htmlFor="reschedule-time"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Time{' '}
                  <span className="text-zinc-400 dark:text-zinc-500 font-normal">(optional)</span>
                </span>
              </label>
              <input
                id="reschedule-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                disabled={isPending}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-800">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!date || !hasChanges || isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? 'Saving...' : 'Reschedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
